import { snapToPixel } from '../util/math.js';
import { fieldMetrics } from '../field/metrics.js';
import { centerSnapBallPoint } from '../player/model.js';
import type { ArcadeReplayFrameSnapshot, ArcadeReplayPackage } from '../../arcade/contracts.js';
import {
  applyArcadeReplayFrameToPlayers,
  blendArcadeReplayPlayersFromShell,
  defaultArcadeOwnerBallPointForReplayFrame,
  extendTouchdownPresentationFrames,
  mapArcadeFieldXToWorldX,
  mapArcadeFieldYToWorldY,
} from './arcade-render.js';
import {
  fumbleRecoveryHeadline,
  isDefensiveFumbleRecovery,
} from './helpers.js';
import { presentFumbleTransitionFrames } from './turnover-cadence.js';

const RUN_SHELL_BLEND_FRAMES = 18;
const PERIMETER_RB_RUN_PLAY_IDS = new Set([
  'outside-zone',
  'toss-sweep',
  'crack-toss',
  'speed-option',
  'zone-read',
]);
const PERIMETER_RB_PATH_SMOOTHING_WEIGHTS = [
  { offset: -2, weight: 0.08 },
  { offset: -1, weight: 0.22 },
  { offset: 0, weight: 0.4 },
  { offset: 1, weight: 0.22 },
  { offset: 2, weight: 0.08 },
];
const PERIMETER_RB_PATH_ARC_PIXELS_BY_PLAY_ID: Record<string, number> = {
  'outside-zone': 10,
  'toss-sweep': 16,
  'crack-toss': 14,
  'speed-option': 12,
  'zone-read': 8,
};
const PERIMETER_RB_LANE_SETTLE_PHASE_BY_PLAY_ID: Record<string, number> = {
  'outside-zone': 0.62,
  'toss-sweep': 0.68,
  'crack-toss': 0.7,
  'speed-option': 0.66,
  'zone-read': 0.58,
};

function arcadeRunFramesMatch(left: any, right: any): boolean {
  if (!left || !right) {
    return false;
  }
  if (
    Math.round(left.ball.ballX) !== Math.round(right.ball.ballX)
    || Math.round(left.ball.ballY) !== Math.round(right.ball.ballY)
    || left.offensePlayers.length !== right.offensePlayers.length
    || left.defensePlayers.length !== right.defensePlayers.length
  ) {
    return false;
  }
  const samePlayers = (leftPlayers: any[], rightPlayers: any[]) => rightPlayers.every((player: any, index: number) => {
    const prior = leftPlayers[index];
    return prior
      && prior.role === player.role
      && Math.round(prior.x) === Math.round(player.x)
      && Math.round(prior.y) === Math.round(player.y);
  });
  return samePlayers(left.offensePlayers, right.offensePlayers)
    && samePlayers(left.defensePlayers, right.defensePlayers);
}

function settledArcadeRunFrameIndex(replay: ArcadeReplayPackage): number {
  const settleEvent = replay.events?.find((event: any) =>
    event.type === 'ballcarrier_down'
    || (event.type === 'play_state_enter' && event.payload?.state === 'whistle')
    || event.type === 'touchdown',
  );
  const settleSimFrame = typeof settleEvent?.simFrame === 'number'
    ? settleEvent.simFrame
    : null;
  if (settleSimFrame == null) {
    return -1;
  }
  return replay.frames.findIndex((frame) => frame.simFrame >= settleSimFrame);
}

function runCarrierForFrame(frame: any): any {
  const ownerRole = String(frame?.ball?.arcadeOwnerRole || '');
  if (!ownerRole) {
    return null;
  }
  return frame.offensePlayers.find((player: any) => player.role === ownerRole)
    || frame.defensePlayers.find((player: any) => player.role === ownerRole)
    || null;
}

function nearestOpposingDistance(frame: any, carrier: any): number {
  if (!frame || !carrier) {
    return Number.POSITIVE_INFINITY;
  }
  const carrierPool = frame.offensePlayers.some((player: any) => player.role === carrier.role)
    ? frame.defensePlayers
    : frame.offensePlayers;
  return carrierPool.reduce((nearest: number, player: any) => {
    const distance = Math.hypot(player.x - carrier.x, player.y - carrier.y);
    return Math.min(nearest, distance);
  }, Number.POSITIVE_INFINITY);
}

function inferredSettledArcadeRunFrameIndex(frames: any[]): number {
  const settleWindow = 8;
  for (let index = 24; index <= frames.length - settleWindow; index += 1) {
    const window = frames.slice(index, index + settleWindow);
    const carriers = window.map(runCarrierForFrame);
    if (carriers.some((carrier) => !carrier)) {
      continue;
    }
    if (window.some((frame) => frame?.ball?.arcadeState !== 'carried')) {
      continue;
    }

    const xValues = carriers.map((carrier) => carrier.x);
    const yValues = carriers.map((carrier) => carrier.y);
    const ballXValues = window.map((frame) => Number(frame?.ball?.ballX ?? 0));
    const ballYValues = window.map((frame) => Number(frame?.ball?.ballY ?? 0));
    const xRange = Math.max(...xValues) - Math.min(...xValues);
    const yRange = Math.max(...yValues) - Math.min(...yValues);
    const ballXRange = Math.max(...ballXValues) - Math.min(...ballXValues);
    const ballYRange = Math.max(...ballYValues) - Math.min(...ballYValues);
    const nearestDefender = nearestOpposingDistance(window[window.length - 1], carriers[carriers.length - 1]);
    if (xRange <= 0.6 && yRange <= 0.6 && ballXRange <= 0.6 && ballYRange <= 0.6 && nearestDefender <= 12) {
      return index;
    }
  }
  return -1;
}

function trimSettledArcadeRunFrames(frames: any[], replay: ArcadeReplayPackage): any[] {
  const settleStartIndex = settledArcadeRunFrameIndex(replay);
  if (settleStartIndex < 1) {
    const inferredSettleIndex = inferredSettledArcadeRunFrameIndex(frames);
    if (inferredSettleIndex < 1) {
      return frames;
    }
    const keepCount = Math.min(frames.length, inferredSettleIndex + 4);
    return keepCount < frames.length
      ? frames.slice(0, keepCount)
      : frames;
  }
  if (settleStartIndex < 1) {
    return frames;
  }

  for (let index = settleStartIndex; index < frames.length; index += 1) {
    if (!arcadeRunFramesMatch(frames[index - 1], frames[index])) {
      continue;
    }
    const keepCount = Math.min(frames.length, index + 3);
    return keepCount < frames.length
      ? frames.slice(0, keepCount)
      : frames;
  }
  return frames;
}

function ballPointForReplayFrame(
  replayFrame: ArcadeReplayFrameSnapshot,
  direction: number,
  metrics: ReturnType<typeof fieldMetrics>,
  losX: number,
  offensePlayers: any[],
  defensePlayers: any[],
) {
  const snapper = offensePlayers.find((player) => player.role === 'C');
  if (replayFrame.ball.state === 'snap' && snapper) {
    return {
      ...centerSnapBallPoint(snapper as any, direction, losX, snapper.y + 8),
      arcadeState: replayFrame.ball.state,
      arcadeOwnerRole: replayFrame.ball.ownerRendererRole,
    };
  }

  const ownerPoint = defaultArcadeOwnerBallPointForReplayFrame(
    replayFrame,
    direction,
    metrics,
    [...offensePlayers, ...defensePlayers],
    { handYOffset: 8 },
  );
  if (ownerPoint) {
    return ownerPoint;
  }

  const flightLift = replayFrame.ball.state === 'pitch'
    ? replayFrame.ball.z * 12
    : replayFrame.ball.state === 'handoff'
      ? replayFrame.ball.z * 6
      : replayFrame.ball.z * 4;
  // Replay player coordinates map to sprite anchors; the ball sprite needs to sit at hand height.
  return {
    ballX: mapArcadeFieldXToWorldX(replayFrame.ball.x, direction),
    ballY: snapToPixel(mapArcadeFieldYToWorldY(replayFrame.ball.y, metrics) + 8 - flightLift),
  };
}

function shouldSmoothPerimeterRunRb(play: any, replayFrame: ArcadeReplayFrameSnapshot): boolean {
  if (!PERIMETER_RB_RUN_PLAY_IDS.has(String(play?.playId || ''))) {
    return false;
  }
  const ballState = String(replayFrame.ball?.state || '');
  const rbHasBall = ballState === 'carried' && replayFrame.ball.ownerRendererRole === 'RB';
  if (ballState !== 'handoff' && ballState !== 'pitch' && !rbHasBall) {
    return false;
  }
  const carrier = replayFrame.players.find((player) => player.rendererRole === 'RB');
  if (!carrier || carrier.side !== 'offense') {
    return false;
  }
  if (carrier.engagementState === 'tackled' || carrier.beingTackledBy.length > 0) {
    return false;
  }
  return replayFrame.playState !== 'whistle';
}

function playerFromFrame(frame: any, role: string): any {
  return frame.offensePlayers.find((player: any) => player.role === role)
    || frame.defensePlayers.find((player: any) => player.role === role)
    || null;
}

function smoothPlayerInFrame(frame: any, role: string, x: number, y: number): any {
  const smoothPool = (players: any[]) => players.map((player: any) => player.role === role
    ? { ...player, x: snapToPixel(x), y: snapToPixel(y) }
    : player);
  return {
    ...frame,
    offensePlayers: smoothPool(frame.offensePlayers),
    defensePlayers: smoothPool(frame.defensePlayers),
  };
}

function smoothPerimeterRunCarrierFrames(
  frames: any[],
  replay: ArcadeReplayPackage,
  play: any,
): any[] {
  const playId = String(play?.playId || '');
  if (!PERIMETER_RB_RUN_PLAY_IDS.has(playId)) {
    return frames;
  }
  const eligibleIndexes = replay.frames
    .map((replayFrame, frameIndex) => shouldSmoothPerimeterRunRb(play, replayFrame) ? frameIndex : -1)
    .filter((frameIndex) => frameIndex >= 0);
  const firstEligibleIndex = eligibleIndexes[0] ?? -1;
  const lastEligibleIndex = eligibleIndexes[eligibleIndexes.length - 1] ?? -1;
  if (firstEligibleIndex < 0 || lastEligibleIndex <= firstEligibleIndex) {
    return frames;
  }
  const finalCarrier = playerFromFrame(frames[lastEligibleIndex], 'RB')
    || playerFromFrame(frames[firstEligibleIndex], 'RB');
  const laneSign = finalCarrier && Math.abs(finalCarrier.y - fieldMetrics().centerY) > 1
    ? Math.sign(finalCarrier.y - fieldMetrics().centerY) || 1
    : 1;
  const arcPixels = PERIMETER_RB_PATH_ARC_PIXELS_BY_PLAY_ID[playId] ?? 10;
  const startCarrier = playerFromFrame(frames[firstEligibleIndex], 'RB');
  const settlePhase = PERIMETER_RB_LANE_SETTLE_PHASE_BY_PLAY_ID[playId] ?? 0.64;
  const endLaneY = finalCarrier?.y ?? startCarrier?.y ?? fieldMetrics().centerY;
  const startLaneY = startCarrier?.y ?? endLaneY;

  return frames.map((frame, frameIndex) => {
    const replayFrame = replay.frames[frameIndex];
    if (!replayFrame || !shouldSmoothPerimeterRunRb(play, replayFrame)) {
      return frame;
    }

    let totalWeight = 0;
    let smoothX = 0;
    let smoothY = 0;
    for (const sample of PERIMETER_RB_PATH_SMOOTHING_WEIGHTS) {
      const sampleIndex = frameIndex + sample.offset;
      const sampleReplayFrame = replay.frames[sampleIndex];
      const sampleFrame = frames[sampleIndex];
      if (!sampleFrame || !sampleReplayFrame || !shouldSmoothPerimeterRunRb(play, sampleReplayFrame)) {
        continue;
      }
      const sampleCarrier = playerFromFrame(sampleFrame, 'RB');
      if (!sampleCarrier) {
        continue;
      }
      totalWeight += sample.weight;
      smoothX += sampleCarrier.x * sample.weight;
      smoothY += sampleCarrier.y * sample.weight;
    }

    if (totalWeight <= 0) {
      return frame;
    }

    const phase = Math.max(0, Math.min(1, (frameIndex - firstEligibleIndex) / (lastEligibleIndex - firstEligibleIndex)));
    const lanePhase = Math.max(0, Math.min(1, phase / settlePhase));
    const laneEase = lanePhase * lanePhase * (3 - 2 * lanePhase);
    const curvedLaneY = startLaneY + (endLaneY - startLaneY) * laneEase;
    const rawSmoothedY = smoothY / totalWeight;
    const curveBlend = phase < settlePhase
      ? 0.82
      : Math.max(0, 0.82 * (1 - (phase - settlePhase) / Math.max(0.001, 1 - settlePhase)));
    const arcRamp = Math.max(0, Math.min(1, phase / 0.18));
    const insideOffset = -laneSign * arcPixels * Math.pow(1 - phase, 1.45) * (arcRamp * arcRamp * (3 - 2 * arcRamp));
    return smoothPlayerInFrame(
      frame,
      'RB',
      smoothX / totalWeight,
      rawSmoothedY + (curvedLaneY - rawSmoothedY) * curveBlend + insideOffset,
    );
  });
}

export function buildArcadeRunLiveSequence(context: any): any[] {
  const replay = context.play?.arcadeReplay as ArcadeReplayPackage | undefined;
  if (!replay?.frames?.length) {
    return [];
  }

  const metrics = fieldMetrics();
  const presentedReplayFrames = extendTouchdownPresentationFrames(
    presentFumbleTransitionFrames(replay),
    replay.stopReason === 'touchdown',
  );
  const defensiveFumbleRecovery = isDefensiveFumbleRecovery(context.play);
  const recovererRole = String(context.play?.participants?.recovererRole || replay.participants?.recovererRole || '') || null;
  const fumbleRecoveryEvent = defensiveFumbleRecovery
    ? replay.events.find((event: any) => event?.type === 'fumble_recovered') || null
    : null;
  const recoveryFrameIndex = typeof fumbleRecoveryEvent?.simFrame === 'number'
    ? presentedReplayFrames.findIndex((frame) => frame.simFrame >= fumbleRecoveryEvent.simFrame)
    : -1;
  const recoveryCallout = defensiveFumbleRecovery ? fumbleRecoveryHeadline(context.play) : null;
  const playerFrames = presentedReplayFrames.map((replayFrame) => {
    const offensePlayers = blendArcadeReplayPlayersFromShell(
      context.formation.offense,
      applyArcadeReplayFrameToPlayers(context.formation.offense, replayFrame, context.direction, metrics, {
        movingThreshold: 0.06,
      }),
      replayFrame,
      { blendFrames: RUN_SHELL_BLEND_FRAMES, revealMotionAt: 0.55 },
    );
    const defensePlayers = blendArcadeReplayPlayersFromShell(
      context.formation.defense,
      applyArcadeReplayFrameToPlayers(context.formation.defense, replayFrame, context.direction, metrics, {
        movingThreshold: 0.06,
      }),
      replayFrame,
      { blendFrames: RUN_SHELL_BLEND_FRAMES, revealMotionAt: 0.55 },
    );
    return {
      simFrame: replayFrame.simFrame,
      offensePlayers,
      defensePlayers,
    };
  });
  const presentedReplay = { ...replay, frames: presentedReplayFrames };
  const frames = smoothPerimeterRunCarrierFrames(playerFrames, presentedReplay, context.play).map((frame, frameIndex) => ({
    ...frame,
    ball: ballPointForReplayFrame(
      presentedReplayFrames[frameIndex],
      context.direction,
      metrics,
      context.losX,
      frame.offensePlayers,
      frame.defensePlayers,
    ),
  }));
  const decoratedFrames = frames.map((frame: any, frameIndex: number) => {
    const takeoverFrames = recoveryFrameIndex >= 0 ? frameIndex - recoveryFrameIndex : -1;
    const liveFrame = { ...frame };
    if (recoveryCallout && takeoverFrames >= 0 && takeoverFrames < 18) {
      liveFrame.ribbonMessage = recoveryCallout;
    }
    if (recovererRole && takeoverFrames >= 0 && takeoverFrames < 12) {
      liveFrame.takeawayFlash = {
        role: recovererRole,
        teamType: 'defense',
        intensity: Math.max(0, Math.min(1, 1 - takeoverFrames / 12)),
        color: recoveryCallout?.color,
      };
    }
    const requestedCelebration = context.play?.heroPresentation?.celebration;
    const celebration = replay.stopReason === 'touchdown'
      ? requestedCelebration === 'signature' ? 'signature' : 'none'
      : null;
    const scorerRole = String(
      context.play?.participants?.runnerRole
        || presentedReplayFrames.at(-1)?.ball.ownerRendererRole
        || '',
    );
    const celebrationStart = Math.max(0, frames.length - 42);
    if (replay.stopReason === 'touchdown' && celebration && scorerRole && frameIndex >= celebrationStart) {
      const progress = (frameIndex - celebrationStart) / Math.max(1, frames.length - celebrationStart - 1);
      liveFrame.celebrationMoment = {
        role: scorerRole,
        teamType: 'offense',
        style: celebration,
        playerName: context.play?.heroPresentation?.playerName || null,
        progress,
        intensity: Math.sin(progress * Math.PI),
      };
      liveFrame.offensePlayers = liveFrame.offensePlayers.map((player: any) => player.role === scorerRole
        ? { ...player, moving: false }
        : player);
    }
    return liveFrame;
  });
  return replay.stopReason === 'touchdown'
    ? decoratedFrames
    : trimSettledArcadeRunFrames(decoratedFrames, presentedReplay);
}
